/**
 * @file confirmCard.ts
 * @brief Pure rules for "Confirm with your bank" on a saved card (personal
 *        Billing → Cards and Company Admin → Billing).
 *
 * @details
 * A phone hold that needed 3DS is cancelled and the user (or the company
 * admin) is emailed a link to the cards page with `?confirm=1&card=pm_…`
 * (BACKEND f5e603e, 20-10-2026). The page shows a banner for it and runs the
 * bank check on that card. Which card: the one named in the link if it is
 * still on the list, else the card holds use (the default), else the first.
 *
 * @author DES
 * @date 21-10-2026
 */

const PM = /^pm_[A-Za-z0-9]{1,64}$/;

/** `?confirm=1[&card=pm_…]` from the email link; anything else is not one. */
export function readConfirmParams(search: string): { confirm: boolean; cardId: string | null } {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  if (params.get("confirm") !== "1") return { confirm: false, cardId: null };
  const card = params.get("card");
  return { confirm: true, cardId: card && PM.test(card) ? card : null };
}

/** The card the banner confirms: the link's, else the default, else the first. */
export function pickCardToConfirm<T extends { id: string }>(
  cards: readonly T[],
  { cardId, defaultId }: { cardId: string | null; defaultId: string | null },
): T | null {
  return (
    (cardId ? cards.find((c) => c.id === cardId) : undefined) ??
    (defaultId ? cards.find((c) => c.id === defaultId) : undefined) ??
    cards[0] ??
    null
  );
}

/** The search string with the email's confirm params taken off (after success). */
export function withoutConfirmParams(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete("confirm");
  params.delete("card");
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
